import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ContextualBalloon from '@ckeditor/ckeditor5-ui/src/panel/balloon/contextualballoon';
import clickOutsideHandler from '@ckeditor/ckeditor5-ui/src/bindings/clickoutsidehandler';
import VideoPropertiesFormView from './video-properties-form';
import ClickObserver from '@ckeditor/ckeditor5-engine/src/view/observer/clickobserver';

import '../theme/video-properties.css';

const VIDEODELETE = 'VIDEODELETE';
const VIDEOPROPERTIES = 'videoProperties';
const VIDEOSIZE = 'videoSize';
const VIDEOPOSITION = 'videoPosition';
const VIDEOOPTIONS = 'videoOptions';

export default class VideoPropertiesUI extends Plugin {
	static get requires() {
		return [ContextualBalloon];
	}

	static get pluginName() {
		return 'VideoPropertiesUI';
	}

	init() {
		const editor = this.editor;
		editor.editing.view.addObserver(ClickObserver);

		this.formView = this._createFormView();
		this._balloon = editor.plugins.get(ContextualBalloon);
		this._enableUserBalloonInteractions();
	}

	destroy() {
		super.destroy();

		this.formView.destroy();
	}

	_createFormView() {
		const editor = this.editor;
		const videoPropertiesCommand = editor.commands.get(VIDEOPROPERTIES);
		const videoPropertiesConfig = editor.config.get( VIDEOPROPERTIES );
		const formView = new VideoPropertiesFormView(
			editor.locale,
			videoPropertiesCommand,
			videoPropertiesConfig
		);

		formView.bind('isEnabled').to(videoPropertiesCommand);

		this.listenTo( formView, VIDEOSIZE, () => {
			videoPropertiesCommand.execute(VIDEOSIZE);
		} );

		this.listenTo( formView, VIDEOPOSITION, () => {
			videoPropertiesCommand.execute(VIDEOPOSITION);
		} );

		this.listenTo( formView, VIDEOOPTIONS, () => {
			videoPropertiesCommand.execute(VIDEOOPTIONS);
		} );

		this.listenTo( formView, 'delete', () => {
			videoPropertiesCommand.execute(VIDEODELETE);
			this._closeFormView();
		} );

		this.listenTo( formView, 'close', () => {
			this._closeFormView();
		} );

		formView.keystrokes.set('Esc', (data, cancel) => {
			this._closeFormView();
			cancel();
		});

		return formView;
	}

	_enableUserBalloonInteractions() {
		const viewDocument = this.editor.editing.view.document;

		this.listenTo( viewDocument, 'click', () => {
			const element = this._getVideoElement();

			if ( element ) {
				// Then show panel but keep focus inside editor editable.
				this._showUI();
			}
		} );

		this.editor.keystrokes.set('Esc', (data, cancel) => {
			if (this._isUIVisible) {
				this._hideUI();
				cancel();
			}
		});

		clickOutsideHandler({
			emitter: this.formView,
			activator: () => this._isFormInPanel,
			contextElements: [this._balloon.view.element],
			callback: () => this._hideUI(),
		});
	}

	_addFormView() {
		if (this._isFormInPanel) {
			return;
		}

		//this.formView.disableCssTransitions();

		this._balloon.add({
			view: this.formView,
			position: this._getBalloonPositionData(),
		});

		//this.formView.enableCssTransitions();
	}

	_closeFormView() {
		const videoPropertiesCommand = this.editor.commands.get(VIDEOPROPERTIES);

		if (videoPropertiesCommand.value !== undefined) {
			this._removeFormView();
		} else {
			this._hideUI();
		}
	}

	_removeFormView() {
		if (this._isFormInPanel) {
			this._balloon.remove(this.formView);

			this.editor.editing.view.focus();
		}
	}

	_showUI(forceVisible = false) {
		this._addFormView();

		if (forceVisible) {
			this._balloon.showStack('main');
		}

		this._startUpdatingUI();
	}

	_hideUI() {
		if (!this._isFormInPanel) {
			return;
		}

		const editor = this.editor;

		this.stopListening(editor.ui, 'update');
		this.stopListening(this._balloon, 'change:visibleView');

		editor.editing.view.focus();

		this._balloon.remove(this.formView);

		editor.editing.view.focus();
	}

	_startUpdatingUI() {
		const editor = this.editor;

		const update = () => {
			const selectedElement = this._getVideoElement();
			if (selectedElement && this._isUIVisible) {
				this._balloon.updatePosition(this._getBalloonPositionData());
			}
		};

		this.listenTo(editor.ui, 'update', update);
		this.listenTo(this._balloon, 'change:visibleView', update);
	}

	get _isFormInPanel() {
		return this._balloon.hasView(this.formView);
	}

	get _isUIVisible() {
		const visibleView = this._balloon.visibleView;
		return visibleView == this.formView;
	}

	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		let target = null;

		target = () => {
			const targetElement = this._getVideoElement();

			return targetElement
				? view.domConverter.mapViewToDom(targetElement)
				: view.domConverter.viewRangeToDom(
						viewDocument.selection.getFirstRange()
				  );
		};

		return { target };
	}

	// Get video element from the view
	_getVideoElement() {
		const view = this.editor.editing.view;
		const selection = view.document.selection;
		const element = selection.getSelectedElement();
		const mediaElement = element ? element.getChild(0) : null;

		if (mediaElement && mediaElement.name === 'video')
			return element;

		return null;
	}
}
